import { useCallback, useEffect, useState } from 'react'
import { useNotify } from 'react-admin'

import { IContractDates, getContractDates, setContractDates } from '../../../Services/contractDates'

/**
 * Charge et enregistre les dates de contrat d'un compte.
 *
 * Le hook porte l'état réseau ; le formulaire ne porte que la saisie. Sans cette
 * séparation, la carte de statut accumulerait chargement, brouillon et
 * sauvegarde dans un seul composant (cf. .claude/rules/react-components.md).
 */
export const useContractDates = (billingAccountId: string, enabled: boolean) => {
	const notify = useNotify()
	const [contract, setContract] = useState<IContractDates | null>(null)
	const [saving, setSaving] = useState(false)

	const load = useCallback(() => {
		if (!billingAccountId || !enabled) return
		getContractDates(billingAccountId)
			.then(setContract)
			.catch(() => setContract(null))
	}, [billingAccountId, enabled])

	useEffect(load, [load])

	const save = useCallback(
		async (dates: { startAt?: string; endAt?: string; anniversary?: string }) => {
			setSaving(true)
			try {
				// La réponse porte l'année recalculée : on la prend telle quelle
				// plutôt que de rejouer le découpage en JavaScript, où les pièges de
				// fuseau et de 29 février se paient une deuxième fois.
				setContract(await setContractDates(billingAccountId, dates))
				notify('Dates du contrat enregistrées', { type: 'success' })
			} catch (e: any) {
				// Le back renvoie un message fait pour être lu tel quel (date
				// illisible, fin avant début, compte piloté par Stripe).
				notify(e?.message || 'Échec de l’enregistrement', { type: 'error' })
			} finally {
				setSaving(false)
			}
		},
		[billingAccountId, notify]
	)

	return { contract, save, saving, reload: load }
}
